"use client"

import posthog, { isPosthogInitialized } from "@/app/infra/posthog"
import { EvmSdkContainerMoleculeReatom } from "@/app/sdk/blockchains/evm-sdk-container-molecule.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import * as amplitude from "@amplitude/analytics-browser"
import { Trans } from "@lingui/react/macro"
import {
  useCreateWallet,
  useLinkAccount,
  usePrivy,
  useWallets,
} from "@privy-io/react-auth"
import {
  useCreateWallet as useCreateSolanaWallet,
  useWallets as useSolanaWallets,
} from "@privy-io/react-auth/solana"
import { useSetActiveWallet } from "@privy-io/wagmi"
import { Button } from "@rarible_int/anaconda_ui_components"
import { wrap } from "@reatom/core"
import { reatomComponent, useWrap } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { formatEther, isAddress } from "viem"
import { useDisconnect, usePublicClient, useWalletClient } from "wagmi"
import {
  resolveEvmWalletAddress,
  resolveLinkedEvmAddress,
  resolveLinkedExternalEvmAddress,
  resolveLinkedSolanaAddress,
  resolveSolanaWalletAddress,
} from "../connected-addresses"
import {
  type EvmReconnectResult,
  WalletConnectMoleculeReatom,
} from "../store.reatom"
import {
  getGlobalWalletAddress,
  selectProfileEvmWallet,
} from "./profile-evm-wallet"

export const PrivyWalletConnectProviderReatom = reatomComponent(
  (props: { children: React.ReactNode }) => {
    return (
      <>
        <PrivyWalletConnectInitializerReatom />
        {props.children}
      </>
    )
  },
  "PrivyWalletConnectProviderReatom",
)

const CONNECT_SUCCESS_EVENT_NAME = "CONNECT_SUCCESS"

const PrivyWalletConnectInitializerReatom = reatomComponent(() => {
  const {
    evmAddress: evmAddressAtom,
    solanaAddress: solanaAddressAtom,
    walletBlockchainNetworkId,
    logIn: logInAtom,
    logOut: logOutAtom,
    linkAdditionalWallet: linkAdditionalWalletAtom,
    getBalanceAtom,
    status,
    connectWalletButton,
    getPublicClient: getPublicClientAtom,
    initialized,
    loginMethod,
  } = useMolecule(WalletConnectMoleculeReatom)

  const { login, logout, authenticated, ready, user, connectWallet } =
    usePrivy()
  const { createWallet } = useCreateWallet()
  const { createWallet: createSolanaWallet } = useCreateSolanaWallet()
  const { linkWallet } = useLinkAccount()
  const { wallets, ready: walletsReady } = useWallets()
  const { wallets: solanaWallets } = useSolanaWallets()
  const { disconnect } = useDisconnect()
  const { setActiveWallet } = useSetActiveWallet()
  const { clearSdk } = useMolecule(EvmSdkContainerMoleculeReatom)
  const logger = useMolecule(LoggerMolecule)

  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()
  const wallet = wallets.length > 0 ? wallets[0] : null
  const connectedSolanaAddress = useMemo(
    () => solanaWallets.find((item) => item.address?.trim())?.address,
    [solanaWallets],
  )
  const globalWalletAddress = useMemo(
    () => getGlobalWalletAddress(user),
    [user],
  )
  const embeddedWalletCreationRef = useRef({
    userId: authenticated ? user?.id : undefined,
    requested: new Set<string>(),
    running: false,
  })
  const currentUserRef = useRef(user)
  currentUserRef.current = authenticated && ready ? user : null
  const identifiedPrivyUserIdRef = useRef<string | undefined>(undefined)

  const resolvedEvmAddress = useMemo(
    () =>
      resolveEvmWalletAddress({
        user,
        globalWalletAddress,
        walletClientAddress: walletClient?.account?.address,
        connectedWalletAddress: wallet?.address,
      }),
    [
      user,
      globalWalletAddress,
      walletClient?.account?.address,
      wallet?.address,
    ],
  )

  const resolvedSolanaAddress = useMemo(
    () =>
      resolveSolanaWalletAddress({
        user,
        connectedSolanaAddress,
      }),
    [user, connectedSolanaAddress],
  )

  const logIn = useCallback(() => login(), [login])
  const logOut = useCallback(() => logout(), [logout])

  const setInitialized = useWrap(() => initialized.set(ready))
  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    setInitialized()
  }, [ready, setInitialized])

  useEffect(() => {
    if (!isPosthogInitialized || !ready) {
      return
    }

    const privyUserId = authenticated ? user?.id : undefined
    const previousPrivyUserId = identifiedPrivyUserIdRef.current

    if (!privyUserId) {
      if (previousPrivyUserId) {
        posthog.reset()
      }
      identifiedPrivyUserIdRef.current = undefined
      return
    }

    if (previousPrivyUserId && previousPrivyUserId !== privyUserId) {
      posthog.reset()
    }

    if (previousPrivyUserId !== privyUserId) {
      posthog.identify(privyUserId)
    }

    identifiedPrivyUserIdRef.current = privyUserId
  }, [authenticated, ready, user?.id])

  useEffect(() => {
    if (!authenticated || !ready || !user || wallets.length === 0) {
      return
    }

    const activeWallet = selectProfileEvmWallet({ user, wallets })
    if (
      activeWallet &&
      walletClient?.account?.address?.toLowerCase() !==
        activeWallet.address.toLowerCase()
    ) {
      void setActiveWallet(activeWallet)
    }
  }, [
    authenticated,
    ready,
    user,
    wallets,
    walletClient?.account?.address,
    setActiveWallet,
  ])

  const updateWalletNetwork = useWrap(() => {
    walletBlockchainNetworkId.set(walletClient?.chain?.id)
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateWalletNetwork()
  }, [walletClient, walletClient?.chain?.id, updateWalletNetwork])

  const updateLoginMethod = useWrap(() => {
    if (!authenticated || !user) {
      loginMethod.set(undefined)
      return
    }

    const hasEmailAccount = user.linkedAccounts?.some(
      (account) => account.type === "email",
    )

    if (hasEmailAccount) {
      loginMethod.set("email")
    } else if (globalWalletAddress) {
      loginMethod.set("global_wallet")
    } else {
      loginMethod.set("wallet")
    }
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateLoginMethod()
  }, [user, authenticated, globalWalletAddress, updateLoginMethod])

  useEffect(() => {
    const userId = authenticated && ready ? user?.id : undefined
    if (embeddedWalletCreationRef.current.userId !== userId) {
      embeddedWalletCreationRef.current = {
        userId,
        requested: new Set(),
        running: false,
      }
    }
    if (!userId || !user) return

    const canCreateEmbeddedWallets = user.linkedAccounts.some(
      (account) =>
        account.type === "email" ||
        (account.type === "wallet" &&
          (account.walletClientType === "privy" ||
            account.walletClientType === "privy-v2" ||
            account.connectorType === "embedded")),
    )
    if (!canCreateEmbeddedWallets) return

    const creation = embeddedWalletCreationRef.current
    if (creation.running) return
    const missingWallets = [
      {
        chain: "ethereum",
        resolveAddress: resolveLinkedEvmAddress,
        create: createWallet,
      },
      {
        chain: "solana",
        resolveAddress: resolveLinkedSolanaAddress,
        create: createSolanaWallet,
      },
    ].filter(({ chain, resolveAddress }) => {
      if (resolveAddress(user) || creation.requested.has(chain)) return false
      creation.requested.add(chain)
      return true
    })

    // Reserve both chains before starting: Privy updates user during creation.
    // Run sequentially so the SDK never opens two creation flows at once.
    const createMissingWallets = async () => {
      for (const wallet of missingWallets) {
        if (embeddedWalletCreationRef.current !== creation) return
        const currentUser = currentUserRef.current
        if (!currentUser || currentUser.id !== userId) return
        if (wallet.resolveAddress(currentUser)) continue
        try {
          await wrap(wallet.create())
        } catch {
          creation.requested.delete(wallet.chain)
        }
      }
    }
    creation.running = true
    void createMissingWallets().finally(() => {
      creation.running = false
    })
  }, [authenticated, ready, user, createWallet, createSolanaWallet])

  const updateWalletState = useWrap(() => {
    if (!ready) {
      return
    }

    if (!authenticated) {
      evmAddressAtom.set(undefined)
      solanaAddressAtom.set(undefined)
      status.set("disconnected")
      clearSdk()
      return
    }

    if (!globalWalletAddress && wallets.length > 0) {
      const context = { source: "privy", wallet: "PrivyWallet" }
      logger?.api.pushEvent(CONNECT_SUCCESS_EVENT_NAME, context)
      amplitude.track(CONNECT_SUCCESS_EVENT_NAME, context)
    }

    evmAddressAtom.set(resolvedEvmAddress)
    solanaAddressAtom.set(resolvedSolanaAddress)

    if (resolvedEvmAddress || resolvedSolanaAddress) {
      status.set("connected")
      return
    }

    status.set("disconnected")
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateWalletState()
  }, [
    authenticated,
    ready,
    resolvedEvmAddress,
    resolvedSolanaAddress,
    wallets,
    globalWalletAddress,
    logger,
    clearSdk,
    updateWalletState,
  ])

  const updateGetBalance = useWrap(() => {
    getBalanceAtom.set({
      getBalance: async () => {
        const addressToUse = resolvedEvmAddress

        if (
          !authenticated ||
          !addressToUse ||
          !isAddress(addressToUse) ||
          !publicClient
        ) {
          return "0"
        }

        try {
          const balance = await wrap(
            publicClient.getBalance({
              address: addressToUse,
            }),
          )
          return formatEther(balance)
        } catch {
          return "0"
        }
      },
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateGetBalance()
  }, [resolvedEvmAddress, authenticated, publicClient, updateGetBalance])

  const updatePublicClient = useWrap(() => {
    getPublicClientAtom.set({
      getPublicClient: publicClient ? async () => publicClient : undefined,
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updatePublicClient()
  }, [publicClient, updatePublicClient])

  const updateLogIn = useWrap(() => {
    logInAtom.set({ logIn: () => logIn() })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateLogIn()
  }, [logIn, updateLogIn])

  const updateLogOut = useWrap(() => {
    logOutAtom.set({
      logOut: async () => {
        await wrap(logOut())
        disconnect()
      },
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateLogOut()
  }, [logOut, disconnect, updateLogOut])

  const linkEvm = useCallback(
    () => linkWallet({ walletChainType: "ethereum-only" }),
    [linkWallet],
  )
  const linkSolana = useCallback(
    () => linkWallet({ walletChainType: "solana-only" }),
    [linkWallet],
  )
  // A locked extension wallet stays the Profile address but drops out of
  // useWallets(); connecting it again makes the wallet ask to unlock.
  // Embedded and global wallets have no extension to unlock.
  const reconnectEvm = useCallback((): EvmReconnectResult => {
    if (!resolvedEvmAddress) {
      return "unavailable"
    }
    // Before Privy restores wallets, while wagmi catches up with a wallet
    // Privy already sees, or while the global wallet is being built, the
    // signer is on its way rather than locked.
    if (
      !walletsReady ||
      wallets.some(
        (item) => item.address.toLowerCase() === resolvedEvmAddress,
      ) ||
      globalWalletAddress?.toLowerCase() === resolvedEvmAddress
    ) {
      return "restoring"
    }
    // Profile prefers a linked external wallet, so it is the one to unlock.
    if (!resolveLinkedExternalEvmAddress(user)) {
      return "unavailable"
    }
    connectWallet({ walletChainType: "ethereum-only" })
    return "prompted"
  }, [
    walletsReady,
    wallets,
    globalWalletAddress,
    user,
    resolvedEvmAddress,
    connectWallet,
  ])

  const updateLinkAdditionalWallet = useWrap(() => {
    linkAdditionalWalletAtom.set({
      linkEvm,
      linkSolana,
      reconnectEvm,
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: the listed values are the intended re-run triggers; the body calls a stable useWrap callback, so removing them would stop the effect firing on state changes
  useEffect(() => {
    updateLinkAdditionalWallet()
  }, [linkEvm, linkSolana, reconnectEvm, updateLinkAdditionalWallet])

  const updateConnectButton = useWrap(() => {
    connectWalletButton.set({ ConnectWalletButton })
  })

  useEffect(() => {
    updateConnectButton()
  }, [updateConnectButton])

  return null
}, "PrivyWalletConnectInitializerReatom")

const ConnectWalletButton = reatomComponent(() => {
  const { login, authenticated, connectWallet } = usePrivy()
  const logger = useMolecule(LoggerMolecule)

  const handleClick = useCallback(async () => {
    const eventName = "INITIATED"
    logger?.api.pushEvent(eventName, { source: "privy" })
    amplitude.track(eventName, { source: "privy" })

    if (authenticated) {
      connectWallet()
    } else {
      login()
    }
  }, [logger, login, authenticated, connectWallet])

  return (
    <Button onClick={handleClick}>
      <Trans>Login</Trans>
    </Button>
  )
}, "ConnectWalletButton")
